import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, Search, Users, FileCheck, Globe } from 'lucide-react';

const PROCESSING_STEPS = [
  { id: 1, label: 'Adding brand...', icon: FileCheck, duration: 5000 },
  { id: 2, label: 'Fetching backlinks from SEMrush...', icon: Search, duration: 12000 },
  { id: 3, label: 'Classifying affiliates...', icon: Users, duration: 12000 },
  { id: 4, label: 'Checking your website for terms consistency...', icon: Globe, duration: 12000 },
  { id: 5, label: 'Matching terms...', icon: FileCheck, duration: 10000 },
];

const BrandProcessingPopup = ({ isOpen, brandName, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setElapsedTime(0);
      setIsComplete(false);
      return;
    }

    // Timer for elapsed time
    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    // Progress through steps
    let stepTimeout;
    const progressSteps = () => {
      let delay = 0;
      PROCESSING_STEPS.forEach((step, index) => {
        delay += step.duration;
        setTimeout(() => {
          setCurrentStep(index + 1);
        }, delay - step.duration);
      });

      // Complete after all steps (approximately 45 seconds)
      stepTimeout = setTimeout(() => {
        setIsComplete(true);
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 2000);
      }, delay);
    };

    progressSteps();

    return () => {
      clearInterval(timerInterval);
      clearTimeout(stepTimeout);
    };
  }, [isOpen, onComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">
            Processing Brand
          </h3>
          <p className="text-blue-100 text-sm mt-1">
            {brandName || 'Brand'}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Timer */}
          <div className="flex items-center justify-center mb-6">
            <div className="text-center">
              <div className="text-3xl font-mono font-bold text-gray-800">
                {formatTime(elapsedTime)}
              </div>
              <p className="text-sm text-gray-500 mt-1">Elapsed time</p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="space-y-3">
            {PROCESSING_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === index + 1;
              const isCompleted = currentStep > index + 1 || isComplete;
              const isPending = currentStep < index + 1;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                    isActive
                      ? 'bg-blue-50 border border-blue-200'
                      : isCompleted
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-blue-100'
                        : isCompleted
                        ? 'bg-green-100'
                        : 'bg-gray-100'
                    }`}
                  >
                    {isActive ? (
                      <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    ) : isCompleted ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <StepIcon className={`w-4 h-4 ${isPending ? 'text-gray-400' : 'text-gray-600'}`} />
                    )}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? 'text-blue-700'
                        : isCompleted
                        ? 'text-green-700'
                        : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Completion message */}
          {isComplete && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-green-700 font-medium">Processing complete!</p>
              <p className="text-green-600 text-sm mt-1">You can now view live affiliates.</p>
            </div>
          )}

          {/* Info message */}
          {!isComplete && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-700 text-sm text-center">
                Please wait, this process can take up to 2 minutes.
              </p>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className={`h-full transition-all duration-500 ${
              isComplete ? 'bg-green-500' : 'bg-blue-600'
            }`}
            style={{
              width: isComplete ? '100%' : `${(currentStep / PROCESSING_STEPS.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BrandProcessingPopup;
